// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './EmailTemplateEditor.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

// Pretty-print HTML with indentation for the editor textarea
function formatHtml(html) {
    if (!html) return '';
    const INDENT = '  ';
    const VOID = new Set(['area','base','br','col','embed','hr','img','input','link','meta','param','source','track','wbr']);
    const INLINE = new Set(['a','abbr','acronym','b','bdo','big','br','cite','code','dfn','em','i','img','input','kbd','label','map','object','output','q','samp','select','small','span','strong','sub','sup','textarea','time','tt','var']);
    let depth = 0;
    let result = '';
    // Tokenise: tags vs text
    const tokens = html.match(/<!--[\s\S]*?-->|<[^>]+>|[^<]+/g) || [];
    tokens.forEach(token => {
        const trimmed = token.trim();
        if (!trimmed) return;
        if (trimmed.startsWith('<!--')) {
            result += '\n' + INDENT.repeat(depth) + trimmed;
            return;
        }
        if (trimmed.startsWith('</')) {
            const m = trimmed.match(/<\/([a-z0-9]+)/i);
            const tag = m && m[1] ? m[1].toLowerCase() : '';
            if (!INLINE.has(tag)) depth = Math.max(0, depth - 1);
            result += '\n' + INDENT.repeat(depth) + trimmed;
        } else if (trimmed.startsWith('<')) {
            const m = trimmed.match(/<([a-z0-9]+)/i);
            const tag = m && m[1] ? m[1].toLowerCase() : '';
            const selfClose = trimmed.endsWith('/>') || VOID.has(tag);
            result += '\n' + INDENT.repeat(depth) + trimmed;
            if (!selfClose && !INLINE.has(tag)) depth++;
        } else {
            result += '\n' + INDENT.repeat(depth) + trimmed;
        }
    });
    return result.trim();
}

// Mock data for previewing the newsletter template
const MOCK_NEWSLETTER = {
    firstName: "Harivishnu",
    contentType: "Blog Post",
    title: "Top 5 Design Trends of 2025",
    contentPreview: "In this post, I explore the most exciting design trends that are shaping the creative industry this year, from AI-assisted workflows to bold typographic choices...",
    contentUrl: "https://www.harivishnu.in/blog/design/top-5-trends-2025",
    emailSubject: "Top 5 Design Trends of 2025"
};

const DEFAULT_HTML = `<div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto;">
    <p>Hey {{firstName}},</p>
    <p>I've just published a <strong>new {{contentType}}</strong> on my website, and I thought you'd enjoy this one.</p>
    <hr />
    <h3>{{title}}</h3>
    <p>{{contentPreview}}</p>
    <hr />
    <p><a href="{{contentUrl}}">Read / Listen Now →</a></p>
</div>`;

export default function NewsletterTemplateEditor() {
    const { documentId } = useParams();
    const isEditMode = !!documentId;
    const navigate = useNavigate();

    const [template, setTemplate] = useState({
        templateName: '',
        subject: '',
        htmlContent: formatHtml(DEFAULT_HTML),
        isActive: true
    });
    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');

    useEffect(() => {
        if (isEditMode) {
            fetchTemplate();
        } else {
            updatePreview(DEFAULT_HTML);
        }
    }, [documentId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        updatePreview(template.htmlContent);
    }, [template.htmlContent]);

    const fetchTemplate = async () => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(
                API_ENDPOINTS.ADMIN.COLLECTION_GET('EmailTemplates', documentId),
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            const rawText = await res.text();
            const trimmed = rawText ? rawText.trim() : '';
            if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                alert('Empty response from server.');
                return;
            }
            let data;
            try { data = JSON.parse(trimmed); } catch (e) { alert('Failed to parse response.'); return; }

            if (data && data.data) {
                const doc = data.data;
                setTemplate({
                    templateName: doc.templateName || '',
                    subject: doc.subject || '',
                    htmlContent: formatHtml(doc.htmlContent || DEFAULT_HTML),
                    isActive: doc.isActive !== undefined ? doc.isActive : true
                });
            }
        } catch (error) {
            console.error('Error fetching template:', error);
            alert('Failed to load template.');
        } finally {
            setLoading(false);
        }
    };

    const updatePreview = (html) => {
        if (!html) return;
        let rendered = html;
        Object.keys(MOCK_NEWSLETTER).forEach(key => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            rendered = rendered.replace(regex, MOCK_NEWSLETTER[key]);
        });
        setPreviewHtml(rendered);
    };

    const handleSave = async () => {
        if (!template.templateName.trim()) {
            alert('Template name is required.');
            return;
        }
        setSaving(true);
        try {
            const token = sessionStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            };

            let res;
            if (isEditMode) {
                res = await fetch(
                    `${API_BASE_URL}/admin/database/EmailTemplates/${documentId}`,
                    { method: 'PATCH', headers, body: JSON.stringify(template) }
                );
            } else {
                res = await fetch(
                    `${API_BASE_URL}/admin/database/EmailTemplates`,
                    { method: 'POST', headers, body: JSON.stringify(template) }
                );
            }

            const rawText = await res.text();
            const trimmed = rawText ? rawText.trim() : '';
            if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                alert('Empty response from server.');
                return;
            }
            let data;
            try { data = JSON.parse(trimmed); } catch (e) { alert('Unexpected response.'); return; }

            if (res.ok) {
                alert('Template saved successfully!');
                navigate('/admin/database/newsletterdb/EmailTemplates');
            } else {
                alert(data.message || 'Failed to save template.');
            }
        } catch (error) {
            console.error('Error saving template:', error);
            alert('Failed to save template.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-spinner flex min-h-[200px] items-center justify-center text-[0.95rem] text-[#666]">Loading template...</div>;

    return (
        <div className="email-editor-container mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-[16px] px-[12px] py-[12px] xl:grid-cols-[1fr_1fr]">
            <div className="email-editor-form email-editor-form--newsletter rounded-[12px] border border-slider bg-bg p-[14px] shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
                <div className="editor-header mb-[12px] flex items-center justify-between gap-[10px]">
                    <div>
                        <Link
                            to="/admin/database/newsletterdb/EmailTemplates"
                            style={{ color: '#6b7280', textDecoration: 'none', fontSize: '14px' }}
                        >
                            ← Back to EmailTemplates (NewsLetterDb)
                        </Link>
                        <h2 style={{ marginTop: 8 }}>{isEditMode ? 'Edit Template' : 'New Template'}</h2>
                    </div>
                    <button className="save-btn rounded-[8px] border border-slider bg-[#111827] px-[14px] py-[8px] text-[0.9rem] font-[600] text-white" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>

                <div className="form-group mb-[10px] flex flex-col gap-[6px]">
                    <label>Template Name</label>
                    <input
                        type="text"
                        value={template.templateName}
                        onChange={(e) => setTemplate({ ...template, templateName: e.target.value })}
                        placeholder="e.g. Blog and Podcast Notification"
                        disabled={isEditMode}
                    />
                </div>

                <div className="form-group mb-[10px] flex flex-col gap-[6px]">
                    <label>Email Subject</label>
                    <input
                        type="text"
                        value={template.subject}
                        onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                    />
                </div>

                <div className="form-group mb-[10px] flex flex-col gap-[6px]">
                    <label>
                        <input
                            type="checkbox"
                            checked={template.isActive}
                            onChange={(e) => setTemplate({ ...template, isActive: e.target.checked })}
                            style={{ marginRight: 6 }}
                        />
                        Active
                    </label>
                </div>

                <div className="form-group mb-[10px] flex flex-col gap-[6px]">
                    <label>HTML Content</label>
                    <textarea
                        value={template.htmlContent}
                        onChange={(e) => setTemplate({ ...template, htmlContent: e.target.value })}
                    />
                    <small>
                        Available variables: {"{{"}<span>firstName</span>{"}}"},{" "}
                        {"{{"}<span>contentType</span>{"}}"},{" "}
                        {"{{"}<span>title</span>{"}}"},{" "}
                        {"{{"}<span>contentPreview</span>{"}}"},{" "}
                        {"{{"}<span>contentUrl</span>{"}}"},{" "}
                        {"{{"}<span>emailSubject</span>{"}}"}
                    </small>
                </div>
            </div>

            <div className="email-editor-preview rounded-[12px] border border-slider bg-bg p-[14px] shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
                <h3>Live Preview</h3>
                <div className="email-preview-frame h-[70vh] overflow-hidden rounded-[10px] border border-slider bg-white">
                    <iframe
                        title="Newsletter Email Preview"
                        srcDoc={previewHtml}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                    />
                </div>
            </div>
        </div>
    );
}
